import React, { FC, useState, useRef } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { LoadingSpinner } from '../../finding/SbSharedTradeBenefitPlacement/LoadingSpinner';
import { PLACEMENT_TYPE_EDUCATION } from 'dibs-trade-rewards-progress/exports/placementTypes';

import styles from './TradeRewardsMessagingWrapper.scss';

import { TradeRewardsMessagingWrapper_user } from './__generated__/TradeRewardsMessagingWrapper_user.graphql';

type Props = {
    user: TradeRewardsMessagingWrapper_user;
    relay: RelayProp;
};

const RewardsMessageProvider = React.lazy(() =>
    import(
        /* webpackChunkName: "rewards-messaging-provider" */ 'dibs-trade-rewards-progress/exports/RewardsMessageProvider'
    )
);

const SbSharedTradeBenefitPlacement = React.lazy(() =>
    import(
        /* webpackChunkName: "trade-loyalty-messages-placement" */ '../../finding/SbSharedTradeBenefitPlacement/SbSharedTradeBenefitPlacement'
    )
);

const OFFSET_VERTICAL = 300;

const TradeRewardsMessagingWrapperComponent: FC<Props> = ({ relay, user }) => {
    const [displayProvider, setDisplayProvider] = useState(false);
    const messageWrapperRef = useRef<HTMLDivElement | null>(null);

    const handleVisibilityChange = ({ isVisible }: { isVisible: boolean }): void => {
        if (isVisible) {
            setDisplayProvider(true);
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container} ref={messageWrapperRef}>
                {!displayProvider ? (
                    <React.Fragment>
                        <VisibilityTracker
                            onVisibilityChange={handleVisibilityChange}
                            elementRef={messageWrapperRef}
                            observerOptions={{
                                rootMargin: `${OFFSET_VERTICAL}px 0px ${OFFSET_VERTICAL}px 0px`,
                            }}
                        />
                        <LoadingSpinner />
                    </React.Fragment>
                ) : (
                    <React.Suspense fallback={<LoadingSpinner />}>
                        <RewardsMessageProvider
                            placementType={PLACEMENT_TYPE_EDUCATION}
                            tradeFirm={user?.tradeFirm || null}
                        >
                            {({ program, updateLocalStorage }) => {
                                if (!program) {
                                    return <LoadingSpinner />;
                                }

                                return (
                                    <React.Suspense fallback={<LoadingSpinner />}>
                                        <SbSharedTradeBenefitPlacement
                                            environment={relay.environment}
                                            userId={user?.serviceId || ''}
                                            program={program}
                                            updateLocalStorage={updateLocalStorage}
                                        />
                                    </React.Suspense>
                                );
                            }}
                        </RewardsMessageProvider>
                    </React.Suspense>
                )}
            </div>
        </div>
    );
};

export const TradeRewardsMessagingWrapper = createFragmentContainer(
    TradeRewardsMessagingWrapperComponent,
    {
        user: graphql`
            fragment TradeRewardsMessagingWrapper_user on User {
                serviceId
                tradeFirm @include(if: $isTrade) {
                    ...RewardsMessageProvider_tradeFirm
                }
            }
        `,
    }
);
